/**
 * Admin panel for managing public Campaigns. Lives inside the
 * Marketing hub at `/admin/campaigns` as the "Campaigns" tab,
 * alongside Landing Pages.
 */
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Badge } from './ui/badge';
import {
    Plus, Loader2, Trash2, Pencil, Link as LinkIcon, Megaphone, Copy, ExternalLink,
} from 'lucide-react';

import { API_BASE } from '../lib/apiBase';
const API = `${API_BASE}/api`;

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const EMPTY = {
    id: null,
    title: '',
    description: '',
    slug: '',
    product_family: 'vinyl',
    bundle_ids: [],
    default_bundle_id: '',
    starting_step: 'packages',
    is_active: true,
};

const slugify = (s) =>
    (s || '')
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 60);

export default function CampaignsPanel() {
    const [campaigns, setCampaigns] = useState([]);
    const [bundles, setBundles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [draft, setDraft] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const slugTouchedRef = useRef(false);

    const fetchAll = async () => {
        setLoading(true);
        try {
            const [cRes, ...bResults] = await Promise.all([
                axios.get(`${API}/admin/campaigns`, { headers: authHeader() }),
                axios.get(`${API}/product-config/admin/bundles?product_family=vinyl`, { headers: authHeader() }),
                axios.get(`${API}/product-config/admin/bundles?product_family=cd`, { headers: authHeader() }),
                axios.get(`${API}/product-config/admin/bundles?product_family=dvd`, { headers: authHeader() }),
                axios.get(`${API}/product-config/admin/bundles?product_family=bluray`, { headers: authHeader() }),
            ]);
            setCampaigns(cRes.data || []);
            const merged = bResults.flatMap((r) => r.data || []);
            // De-dupe by id (a bundle is in exactly one family but be safe)
            const seen = new Set();
            const unique = [];
            for (const b of merged) {
                if (!seen.has(b.id)) { seen.add(b.id); unique.push(b); }
            }
            setBundles(unique);
        } catch (err) {
            toast.error('Failed to load campaigns');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchAll(); }, []);

    const openNew = () => {
        setDraft({ ...EMPTY });
        slugTouchedRef.current = false;
        setModalOpen(true);
    };

    const openEdit = (c) => {
        setDraft({
            id: c.id,
            title: c.title,
            description: c.description || '',
            slug: c.slug,
            product_family: c.product_family || 'vinyl',
            bundle_ids: [...(c.bundle_ids || [])],
            default_bundle_id: c.default_bundle_id || '',
            starting_step: c.starting_step || 'packages',
            is_active: c.is_active !== false,
        });
        slugTouchedRef.current = true;
        setModalOpen(true);
    };

    const handleSave = async () => {
        if (!draft.title.trim() || !draft.slug.trim()) {
            toast.error('Title and slug are required');
            return;
        }
        setSaving(true);
        try {
            const payload = {
                title: draft.title.trim(),
                description: draft.description.trim() || null,
                slug: draft.slug.trim(),
                product_family: draft.product_family,
                bundle_ids: draft.bundle_ids,
                default_bundle_id: draft.default_bundle_id || null,
                starting_step: draft.starting_step,
                is_active: draft.is_active,
            };
            if (draft.id) {
                await axios.put(`${API}/admin/campaigns/${draft.id}`, payload, { headers: authHeader() });
                toast.success('Campaign updated');
            } else {
                await axios.post(`${API}/admin/campaigns`, payload, { headers: authHeader() });
                toast.success('Campaign created');
            }
            setModalOpen(false);
            await fetchAll();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (c) => {
        if (!window.confirm(`Delete campaign "${c.title}"?`)) return;
        try {
            await axios.delete(`${API}/admin/campaigns/${c.id}`, { headers: authHeader() });
            toast.success('Campaign deleted');
            await fetchAll();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Delete failed');
        }
    };

    const toggleBundle = (bundleId) => {
        setDraft((d) => {
            const has = d.bundle_ids.includes(bundleId);
            const next = has ? d.bundle_ids.filter((x) => x !== bundleId) : [...d.bundle_ids, bundleId];
            const default_bundle_id = next.includes(d.default_bundle_id) ? d.default_bundle_id : '';
            return { ...d, bundle_ids: next, default_bundle_id };
        });
    };

    const handleTitleChange = (val) => {
        setDraft((d) => {
            const next = { ...d, title: val };
            if (!slugTouchedRef.current && !d.id) next.slug = slugify(val);
            return next;
        });
    };

    const copyLink = (slug) => {
        const url = `${window.location.origin}/campaigns/${slug}`;
        navigator.clipboard.writeText(url).then(() => toast.success('Link copied'));
    };

    const filteredBundles = bundles.filter((b) => b.product_family === draft.product_family);

    return (
        <div className="space-y-4">
            <Card>
                <CardHeader>
                    <div className="flex items-start justify-between gap-4">
                        <div>
                            <CardTitle className="flex items-center gap-2">
                                <Megaphone className="h-5 w-5 text-indigo-500" />
                                Campaigns
                            </CardTitle>
                            <CardDescription>
                                Public marketing entry points — share a campaign link from Lovable or your social channels and visitors land directly in the Quick Order flow.
                            </CardDescription>
                        </div>
                        <Button onClick={openNew} data-testid="campaigns-new-btn">
                            <Plus className="h-4 w-4 mr-2" />
                            New campaign
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
                    ) : campaigns.length === 0 ? (
                        <div className="text-center py-10 border-2 border-dashed border-slate-200 rounded-lg">
                            <Megaphone className="h-10 w-10 mx-auto text-slate-300 mb-3" />
                            <p className="text-sm text-slate-500">No campaigns yet. Create one to generate a public Quick Order link.</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {campaigns.map((c) => (
                                <div
                                    key={c.id}
                                    className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-white"
                                    data-testid={`campaign-row-${c.id}`}
                                >
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <p className="text-sm font-semibold text-slate-900 truncate">{c.title}</p>
                                            {c.is_active ? (
                                                <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]">Active</Badge>
                                            ) : (
                                                <Badge className="bg-slate-100 text-slate-600 border-0 text-[10px]">Inactive</Badge>
                                            )}
                                            <Badge variant="outline" className="text-[10px] capitalize">{c.product_family}</Badge>
                                            <Badge variant="outline" className="text-[10px]">
                                                Starts at {c.starting_step === 'quantity' ? 'How many copies?' : 'Choose Package'}
                                            </Badge>
                                        </div>
                                        <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                                            <LinkIcon className="h-3 w-3" />
                                            <span className="font-mono truncate">/campaigns/{c.slug}</span>
                                            <span className="text-slate-400">·</span>
                                            <span>{c.bundle_ids.length} bundle(s)</span>
                                        </div>
                                    </div>
                                    <Button variant="ghost" size="sm" onClick={() => copyLink(c.slug)} title="Copy public link" data-testid={`campaign-copy-${c.id}`}>
                                        <Copy className="h-4 w-4" />
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => window.open(`/campaigns/${c.slug}`, '_blank')} title="Open public link" data-testid={`campaign-open-${c.id}`}>
                                        <ExternalLink className="h-4 w-4" />
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => openEdit(c)} data-testid={`campaign-edit-${c.id}`}>
                                        <Pencil className="h-4 w-4" />
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => handleDelete(c)} className="text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`campaign-delete-${c.id}`}>
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Create / Edit Modal */}
            <Dialog open={modalOpen} onOpenChange={setModalOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="campaign-modal">
                    <DialogHeader>
                        <DialogTitle>{draft.id ? 'Edit campaign' : 'Create campaign'}</DialogTitle>
                        <DialogDescription>
                            Configure the public landing page and which Quick Order bundles are shown.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <div className="col-span-2">
                                <Label htmlFor="c-title">Title</Label>
                                <Input
                                    id="c-title"
                                    value={draft.title}
                                    onChange={(e) => handleTitleChange(e.target.value)}
                                    placeholder="Pre-order — Single Edition"
                                    data-testid="campaign-title-input"
                                />
                            </div>
                            <div className="col-span-2">
                                <Label htmlFor="c-slug">URL slug</Label>
                                <div className="flex items-center gap-1">
                                    <span className="text-sm text-slate-500 whitespace-nowrap">/campaigns/</span>
                                    <Input
                                        id="c-slug"
                                        value={draft.slug}
                                        onChange={(e) => { slugTouchedRef.current = true; setDraft({ ...draft, slug: slugify(e.target.value) }); }}
                                        placeholder="preorder-single"
                                        data-testid="campaign-slug-input"
                                    />
                                </div>
                            </div>
                            <div className="col-span-2">
                                <Label htmlFor="c-desc">Description</Label>
                                <Textarea
                                    id="c-desc"
                                    value={draft.description}
                                    onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                                    placeholder="A short tagline shown on the landing page."
                                    rows={2}
                                    data-testid="campaign-description-input"
                                />
                            </div>
                            <div>
                                <Label>Product family</Label>
                                <Select
                                    value={draft.product_family}
                                    onValueChange={(v) => setDraft({ ...draft, product_family: v, bundle_ids: [], default_bundle_id: '' })}
                                >
                                    <SelectTrigger data-testid="campaign-family-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="vinyl">Vinyl</SelectItem>
                                        <SelectItem value="cd">CD</SelectItem>
                                        <SelectItem value="dvd">DVD</SelectItem>
                                        <SelectItem value="bluray">Blu-ray</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Starting step</Label>
                                <Select
                                    value={draft.starting_step}
                                    onValueChange={(v) => setDraft({ ...draft, starting_step: v })}
                                >
                                    <SelectTrigger data-testid="campaign-step-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="packages">Choose Your Package</SelectItem>
                                        <SelectItem value="quantity">How many copies?</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {/* Bundle picker */}
                        <div>
                            <Label className="mb-2 block">Allowed bundles ({draft.bundle_ids.length} selected)</Label>
                            <div className="border border-slate-200 rounded-lg max-h-72 overflow-y-auto divide-y">
                                {filteredBundles.length === 0 ? (
                                    <p className="text-sm text-slate-500 p-3">No bundles available for this product family. Create one in Quick Bundles first.</p>
                                ) : filteredBundles.map((b) => {
                                    const checked = draft.bundle_ids.includes(b.id);
                                    return (
                                        <label
                                            key={b.id}
                                            className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-50 ${checked ? 'bg-amber-50/40' : ''}`}
                                            data-testid={`campaign-bundle-row-${b.id}`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={() => toggleBundle(b.id)}
                                                className="h-4 w-4 rounded border-slate-300"
                                                data-testid={`campaign-bundle-toggle-${b.id}`}
                                            />
                                            <div className="flex-1">
                                                <p className="text-sm font-medium text-slate-900">{b.title}</p>
                                                <p className="text-xs text-slate-500">{b.format_label} · {b.tier || 'standard'}</p>
                                            </div>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Default bundle (only if quantity-start) */}
                        {draft.starting_step === 'quantity' && draft.bundle_ids.length > 0 && (
                            <div>
                                <Label>Default bundle (used when no ?bundle=... in URL)</Label>
                                <Select
                                    value={draft.default_bundle_id || ''}
                                    onValueChange={(v) => setDraft({ ...draft, default_bundle_id: v })}
                                >
                                    <SelectTrigger data-testid="campaign-default-bundle-select"><SelectValue placeholder="Pick a default bundle" /></SelectTrigger>
                                    <SelectContent>
                                        {draft.bundle_ids.map((bid) => {
                                            const b = bundles.find((x) => x.id === bid);
                                            return <SelectItem key={bid} value={bid}>{b?.title || bid}</SelectItem>;
                                        })}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <div>
                                <Label htmlFor="c-active" className="font-medium">Active</Label>
                                <p className="text-xs text-slate-500">When off, the public link returns 404.</p>
                            </div>
                            <Switch
                                id="c-active"
                                checked={draft.is_active}
                                onCheckedChange={(v) => setDraft({ ...draft, is_active: v })}
                                data-testid="campaign-active-toggle"
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setModalOpen(false)} disabled={saving}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSave}
                            disabled={saving}
                            className="text-white border-0 shadow-md"
                            style={{ backgroundImage: 'linear-gradient(135deg, hsl(35 92% 45%), hsl(42 96% 55%))' }}
                            data-testid="campaign-save-btn"
                        >
                            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            {draft.id ? 'Save changes' : 'Create campaign'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
